import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAppSelector } from "../../../../utils/hooks";
import {
  selectEmail,
  selectName,
  selectRole,
  selectUserId,
  selectIsSuperAdmin,
} from "../../../../redux/authSlice";
import urls from "../../../../global/constants/UrlConstants";
import YhButton from "../../../../ui/YhButton";
import PersonalDetails from "../../../Team/components/stepper/Personal Details/PersonalDetails";
import OfficialInformation from "../../../Team/components/stepper/OfficialInformation/OfficialInformation";
import BankInformation from "../../../Team/components/stepper/BankInformation/BankInformation";
import EmployeeDocuments from "../../../Team/components/stepper/EmployeeDocuments/EmployeeDocuments";
import LeaveDetails from "../../../Team/components/stepper/LeaveDetails/LeaveDetails";
import SalaryInformation from "../../../Team/components/stepper/SalaryInformation/SalaryInformation";
import { StepActions } from "../../../Team/components/AddNewTeam/AddNewTeam";
import { getPerosnalDetails } from "../../../Team/components/stepper/StepperService";

const SECTIONS = [
  { id: "personal", label: "Personal", desc: "Basic info & contact" },
  { id: "official", label: "Official", desc: "Role & organization" },
  { id: "bank", label: "Bank", desc: "Bank account details" },
  { id: "documents", label: "Documents", desc: "ID & certificates" },
  { id: "leave", label: "Leave", desc: "Leave balances" },
  { id: "salary", label: "Salary", desc: "Compensation" },
];

const AccountProfilePage = () => {
  const history = useHistory();
  const userId = useAppSelector(selectUserId);
  const userName = useAppSelector(selectName);
  const email = useAppSelector(selectEmail);
  const role = useAppSelector(selectRole);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const isAdmin = useAppSelector((state) => !!state.auth.isAdmin);
  const canEditAll = isAdmin || isSuperAdmin;

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(
    () => new Set()
  );
  const [saving, setSaving] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const stepActionsRef = useRef<StepActions | null>(null);
  const activeStepRef = useRef(0);

  useEffect(() => {
    activeStepRef.current = activeStep;
    stepActionsRef.current = null;
  }, [activeStep]);

  useEffect(() => {
    const loadAvatar = async () => {
      if (!userId) return;
      try {
        const res = await getPerosnalDetails(userId);
        const img = res?.data?.profile_image || res?.data?.[0]?.profile_image;
        if (img) setProfileImage(img);
      } catch {
        /* ignore */
      }
    };
    loadAvatar();
  }, [userId]);

  const registerStepActions = useCallback((actions: StepActions) => {
    stepActionsRef.current = actions;
  }, []);

  const handleClose = () => {
    if (history.length > 1) history.goBack();
    else history.push(urls.accountSettingsViewPth);
  };

  /** Only called after a successful Save — marks step complete and advances */
  const nextStep = () => {
    const current = activeStepRef.current;
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.add(current);
      return next;
    });
    setActiveStep((s) => Math.min(SECTIONS.length - 1, s + 1));
  };

  const backStep = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  /** Sidebar / chips: view only — never save or mark complete */
  const goToStep = (index: number) => {
    if (index === activeStepRef.current) return;
    setActiveStep(index);
  };

  const handleFooterBack = () => {
    if (activeStep === 0) {
      handleClose();
      return;
    }
    backStep();
  };

  const handleFooterSave = async () => {
    if (!stepActionsRef.current?.submit) return;
    try {
      setSaving(true);
      await stepActionsRef.current.submit();
    } finally {
      setSaving(false);
    }
  };

  const initial = (userName || "U").trim().charAt(0).toUpperCase();

  const getContent = () => {
    const common = {
      userId,
      nextStep,
      backStep,
      fromSettingOrLogout: "fromSettingOrLogout",
      registerStepActions,
      hideFooterButtons: true,
    };

    switch (activeStep) {
      case 0:
        return (
          <PersonalDetails
            {...common}
            setUserId={() => {}}
            onProfileImageChange={(url) => setProfileImage(url)}
          />
        );
      case 1:
        return <OfficialInformation {...common} />;
      case 2:
        return <BankInformation {...common} />;
      case 3:
        return <EmployeeDocuments {...common} />;
      case 4:
        return <LeaveDetails {...common} />;
      case 5:
        return (
          <SalaryInformation
            {...common}
            handleCloseAddTeam={handleClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-68px)] flex-col bg-white">
      {/* Hero */}
      <div className="relative shrink-0 overflow-hidden border-b border-[#EEF0F5] bg-white">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#F9F0F4_0%,#FFFFFF_45%,#FFFFFF_100%)]" />
        <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#A02448]/[0.08]" />
        <div className="absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-[#818CF8]/[0.06]" />

        <div className="relative px-4 pb-5 pt-5 sm:px-6 lg:px-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#A02448]">
                Account
              </p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
                My Profile
              </h1>
              <p className="mt-1 text-sm text-[#6B7280]">
                Manage your personal information and preferences
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white/80 text-[#374151] shadow-sm backdrop-blur transition hover:bg-white"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#EEF0F5] bg-white/90 p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] backdrop-blur">
            <div className="relative">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt=""
                  className="h-16 w-16 rounded-2xl object-cover ring-2 ring-[#EEF0FF] sm:h-20 sm:w-20"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#A02448] text-2xl font-bold text-white ring-2 ring-[#EEF0FF] sm:h-20 sm:w-20 sm:text-3xl">
                  {initial}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-bold text-[#111827] sm:text-xl">
                {userName || "User"}
              </h2>
              <p className="mt-0.5 truncate text-sm text-[#6B7280]">
                {email || "—"}
              </p>
              {role ? (
                <span className="mt-2 inline-flex rounded-full bg-[#F9F0F4] px-2.5 py-0.5 text-xs font-semibold text-[#A02448]">
                  {role}
                </span>
              ) : null}
            </div>
            <div className="hidden text-right sm:block">
              <p className="text-xs font-medium uppercase tracking-wide text-[#9CA3AF]">
                Section
              </p>
              <p className="mt-1 text-sm font-semibold text-[#111827]">
                {SECTIONS[activeStep].label}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Body — natural height, no nested fixed scroll */}
      <div className="flex flex-1 gap-4 p-4 sm:p-5 lg:p-6">
        <aside className="sticky top-4 hidden h-fit w-[240px] shrink-0 rounded-2xl border border-[#EEF0F5] bg-white p-3 shadow-[0_4px_20px_rgba(15,23,42,0.04)] lg:block">
          <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
            Profile sections
          </p>
          <ol className="space-y-1">
            {SECTIONS.map((section, index) => {
              const isActive = index === activeStep;
              const isDone = completedSteps.has(index);
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => goToStep(index)}
                    className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                      isActive
                        ? "bg-[#F9F0F4] text-[#841C39]"
                        : "text-[#374151] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                        isActive
                          ? "bg-[#A02448] text-white"
                          : isDone
                            ? "bg-[#D1FAE5] text-[#059669]"
                            : "bg-[#F3F4F6] text-[#6B7280]"
                      }`}
                    >
                      {isDone && !isActive ? "✓" : index + 1}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={`block text-sm ${
                          isActive ? "font-semibold" : "font-medium"
                        }`}
                      >
                        {section.label}
                      </span>
                      <span className="mt-0.5 block text-xs text-[#9CA3AF]">
                        {section.desc}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-3 flex gap-2 overflow-x-auto lg:hidden">
            {SECTIONS.map((section, index) => (
              <button
                key={section.id}
                type="button"
                onClick={() => goToStep(index)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  index === activeStep
                    ? "bg-[#A02448] text-white"
                    : "bg-white text-[#6B7280] ring-1 ring-[#EEF0F5]"
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>

          <section className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
            {!canEditAll && activeStep !== 0 && (
              <div className="mb-3 flex items-center gap-2 rounded-lg bg-[#FEF3C7] px-3 py-2 text-sm text-[#92400E]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                View only — contact your admin to update this section
              </div>
            )}
            <div
              style={
                !canEditAll && activeStep !== 0
                  ? { pointerEvents: "none", opacity: 0.7 }
                  : undefined
              }
            >
              {getContent()}
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-20 flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={handleFooterBack}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          {activeStep > 0 ? (
            <YhButton variant="secondary" size="sm" onClick={handleFooterBack}>
              Back
            </YhButton>
          ) : (
            <YhButton variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </YhButton>
          )}
          {(canEditAll || activeStep === 0) && (
            <YhButton
              variant="primary"
              size="sm"
              loading={saving}
              onClick={handleFooterSave}
            >
              {activeStep === SECTIONS.length - 1 ? "Save" : "Save & Continue"}
            </YhButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountProfilePage;
